import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Play, Clock, Eye, Heart, Trash2, Filter, Search,
  Video, Calendar, User
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STREAM_CATEGORIES = [
  { id: 'gaming', name: 'Gaming', icon: '🎮' },
  { id: 'music', name: 'Music', icon: '🎵' },
  { id: 'just_chatting', name: 'Just Chatting', icon: '💬' },
  { id: 'sports', name: 'Sports', icon: '⚽' },
  { id: 'creative', name: 'Creative', icon: '🎨' },
  { id: 'education', name: 'Education', icon: '📚' },
  { id: 'cooking', name: 'Cooking', icon: '🍳' },
  { id: 'travel', name: 'Travel', icon: '✈️' },
  { id: 'fitness', name: 'Fitness', icon: '💪' },
  { id: 'technology', name: 'Technology', icon: '💻' }
];

const VODLibrary = ({ onClose }) => {
  const [vods, setVods] = useState([]);
  const [myVods, setMyVods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('browse'); // 'browse' or 'my'
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedVod, setSelectedVod] = useState(null);

  useEffect(() => {
    fetchVods();
    fetchMyVods();
  }, [selectedCategory]);

  const fetchVods = async () => {
    setLoading(true);
    try {
      const params = selectedCategory ? `?category=${selectedCategory}` : '';
      const response = await axios.get(`${API}/vods${params}`);
      setVods(response.data.vods || []);
    } catch (error) {
      console.error('Error fetching VODs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyVods = async () => {
    try {
      const response = await axios.get(`${API}/vods/my`);
      setMyVods(response.data || []);
    } catch (error) {
      console.error('Error fetching my VODs:', error);
    }
  };

  const handleDeleteVod = async (vodId) => {
    if (!window.confirm('Are you sure you want to delete this VOD?')) return;
    try {
      await axios.delete(`${API}/vods/${vodId}`);
      fetchMyVods();
    } catch (error) {
      console.error('Error deleting VOD:', error);
    }
  };

  const formatDuration = (startedAt, endedAt) => {
    if (!startedAt || !endedAt) return 'N/A';
    const start = new Date(startedAt);
    const end = new Date(endedAt);
    const diffMs = end - start;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/90 z-50 overflow-y-auto"
      data-testid="vod-library"
    >
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20"
            >
              <X className="w-5 h-5 text-white" />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-white">VOD Library</h2>
              <p className="text-white/60 text-sm">Watch past streams</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('browse')}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              activeTab === 'browse' 
                ? 'bg-purple-500 text-white' 
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            Browse VODs
          </button>
          <button
            onClick={() => setActiveTab('my')}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              activeTab === 'my' 
                ? 'bg-purple-500 text-white' 
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            My VODs ({myVods.length})
          </button>
        </div>

        {/* Category Filter */}
        {activeTab === 'browse' && (
          <div className="flex items-center gap-3 overflow-x-auto pb-4 mb-6">
            <button
              onClick={() => setSelectedCategory('')}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                !selectedCategory 
                  ? 'bg-white text-black' 
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              All
            </button>
            {STREAM_CATEGORIES.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-2 ${
                  selectedCategory === cat.id 
                    ? 'bg-purple-500 text-white' 
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                <span>{cat.icon}</span>
                <span>{cat.name}</span>
              </button>
            ))}
          </div>
        )}

        {/* VODs Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(activeTab === 'browse' ? vods : myVods).map((vod) => {
              const category = STREAM_CATEGORIES.find(c => c.id === vod.category);
              return (
                <motion.div
                  key={vod.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-white/20 transition-colors group"
                >
                  {/* Thumbnail */}
                  <div 
                    className="relative aspect-video cursor-pointer"
                    style={{ background: `linear-gradient(135deg, #8B5CF640, #6366F140)` }}
                    onClick={() => setSelectedVod(vod)}
                  >
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-5xl">{category?.icon || '📺'}</span>
                    </div>
                    
                    {/* Play Overlay */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <Play className="w-8 h-8 text-white fill-white" />
                      </div>
                    </div>

                    {/* Duration Badge */}
                    <div className="absolute bottom-3 right-3 px-2 py-1 rounded bg-black/70 text-white text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDuration(vod.started_at, vod.ended_at)}
                    </div>

                    {/* Category Badge */}
                    <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-purple-500 text-white text-xs">
                      {category?.name || 'Stream'}
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <h3 className="font-semibold text-white mb-1 truncate">{vod.title}</h3>
                    
                    {vod.streamer && (
                      <div className="flex items-center gap-2 text-white/60 text-sm mb-3">
                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold">
                          {vod.streamer.name?.charAt(0).toUpperCase()}
                        </div>
                        <span>{vod.streamer.name}</span>
                        {vod.streamer.is_verified_streamer && (
                          <span className="text-blue-400">✓</span>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between text-white/40 text-xs">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {vod.views || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(vod.ended_at).toLocaleDateString()}
                        </span>
                      </div>

                      {activeTab === 'my' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteVod(vod.id);
                          }}
                          className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {!loading && (activeTab === 'browse' ? vods : myVods).length === 0 && (
          <div className="text-center py-20">
            <Video className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              {activeTab === 'browse' ? 'No VODs Available' : 'No Recordings Yet'}
            </h3>
            <p className="text-white/60">
              {activeTab === 'browse' 
                ? 'Check back later for recorded streams!' 
                : 'Start recording your streams to save them here!'}
            </p>
          </div>
        )}
      </div>

      {/* VOD Player Modal */}
      <AnimatePresence>
        {selectedVod && (
          <VODPlayer vod={selectedVod} onClose={() => setSelectedVod(null)} />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// VOD Player Component
const VODPlayer = ({ vod, onClose }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black z-[60] flex items-center justify-center"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 p-2 rounded-full bg-white/10 hover:bg-white/20 z-10"
      >
        <X className="w-6 h-6 text-white" />
      </button>

      <div className="max-w-5xl w-full mx-4">
        {/* Video Placeholder */}
        <div className="aspect-video bg-gray-900 rounded-2xl flex items-center justify-center mb-4">
          <div className="text-center">
            <Play className="w-20 h-20 text-white/50 mx-auto mb-4" />
            <p className="text-white/70">VOD Playback</p>
            <p className="text-white/40 text-sm">Video streaming would be implemented with a media server</p>
          </div>
        </div>

        {/* Info */}
        <div className="bg-white/5 rounded-2xl p-6">
          <h2 className="text-xl font-bold text-white mb-2">{vod.title}</h2>
          {vod.streamer && (
            <div className="flex items-center gap-3 text-white/70">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                {vod.streamer.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-medium text-white">{vod.streamer.name}</p>
                <p className="text-sm text-white/50">
                  Streamed on {new Date(vod.started_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default VODLibrary;
